import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from "@mui/material";
import { SourceItem } from "../../shared/models/source-item.model";
import { useSiteContext } from '../../state/SiteContext';

interface BaseSelectorProps {
  label: string,
  valueset: Array<SourceItem>,
  initialValues: Array<string>,
  optionlabeltemplate: string,
  autocompleteprops: Record<string, any>,
  textfieldprops: Record<string, any>,
  multiple: boolean,
  onChange: (values: Array<SourceItem>) => void,
}
/***
 * A selector component that allows the user to select a value from a list of values.
 * The list of values is loaded using an ArraySource specification.
 */
export function BaseSelector({ label, valueset, initialValues, optionlabeltemplate, autocompleteprops, textfieldprops, multiple, onChange }: BaseSelectorProps) {
  const { siteState } = useSiteContext();

  const [open, setOpen] = useState<boolean>(false);


  const [values, setValues] = React.useState<Array<SourceItem>>(initialValues.map(
    val => valueset.find(item => item.value === val) || { value: "", props: {} }
  ).filter(item => item.value !== ""));

  useEffect(() => {
    setValues(initialValues.map(
      val => valueset.find(item => item.value === val) || { value: "", props: {} }
    ).filter(item => item.value !== ""));
  }, [initialValues, valueset]);

  const evaluatePresentation = useCallback((val: SourceItem): string => {
    if (optionlabeltemplate) {
      return optionlabeltemplate
        .replace(/\{value\}/g, val.value.toString())
        .replace(/\{position\}/g, val.position?.toString() || "")
        .replace(/\{label:lang\}/g, siteState.site?.languages?.find(lang => lang.code === val.value)?.label || val.value.toString())
        .replace(/\{label\}/g, siteState.site?.labels?.[val.value.toString()] || val.value.toString())
        .replace(/\{property:(.*)\.(.*)\}/gs, (match: string, p1: string, p2: string) => val.props?.[p1]?.[p2]?.toString() || "");
    }
    return val.value.toString();
  }, [optionlabeltemplate, siteState.site]);

  const optionLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {};
    for (let val of valueset) {
      labels[val.value.toString()] = evaluatePresentation(val);
    }
    return labels;
  }, [valueset, evaluatePresentation]);

  const output = useMemo(() => {
    return <Autocomplete {...autocompleteprops}
      open={open}
      disablePortal={true}
      onOpen={async () => { setOpen(true) }}
      onClose={() => { setOpen(false) }}
      options={valueset || []}
      getOptionLabel={(option: SourceItem) => optionLabels[option.value.toString()] || option.value.toString()}
      isOptionEqualToValue={(option: SourceItem, val: SourceItem) => option.value === val.value}
      multiple={multiple}
      value={multiple ? values : (values[0] || null)}
      onChange={(event: React.SyntheticEvent<Element, Event>, newVal: any) => {
        if (multiple) {
          setValues(newVal as Array<SourceItem>);
          onChange(newVal as Array<SourceItem>);
        }
        else {
          setValues([newVal as SourceItem]);
          onChange(newVal ? [newVal as SourceItem] : []);
        }
      }}
      renderInput={(inputParams: any) => {
        return <TextField
          {...inputParams}
          {...textfieldprops}
          label={label} />
      }}
    />;
  }, [valueset, label, open, multiple, values, textfieldprops, optionLabels, onChange, autocompleteprops]);

  return <>
    {output}
  </>;
}

export const collectProps = (children: React.ReactNode): Record<string, string> => {
  const props: Record<string, string> = {};
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      React.Children.forEach(child.props.children, (propsChild) => {
        if (React.isValidElement(propsChild)) {
          const ReactElement = propsChild as React.ReactElement;
          if (ReactElement.type === "arg-property") {
            const propertyName = ReactElement.props.name;
            const propertyValue = ReactElement.props.value;
            props[propertyName] = propertyValue;
          }
        }
      });
    }
  });
  return props;
}
